import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "./fm";
import { Hexagon } from "lucide-react";
import ParticleField from "./ParticleField";

const OFFSET_MS = 8 * 60 * 60 * 1000;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const pad = (n) => String(n).padStart(2, "0");

/**
 * Intentionally NO network calls for the sign-in page live clock.
 *
 * A display clock on a login page never needs sub-second accuracy; the user's
 * local device clock (kept accurate by the operating system's NTP daemon on
 * 99.99% of phones and laptops) is entirely sufficient for a "what time is it"
 * chip in the header.
 *
 * History of earlier attempts (why this function is now a no-op):
 *   v1  worldtimeapi.org / timeapi.io
 *          → 3rd-party rate-limited services.  Abort on React StrictMode
 *            double-effect produced Chrome red `ERR_CONNECTION_RESET` logs.
 *   v2  HEAD /cdn-cgi/trace
 *          → Cloudflare-only "magic" path.  Returned HTTP 404 on every non-CF
 *            origin (Emergent previews, Vercel, plain localhost).  StrictMode
 *            abort produced `ERR_ABORTED 404 (Not Found)` red console lines.
 *   v3  GET /api/health
 *          → own backend.  When the backend is actually DOWN / restarting /
 *            recovering from a server.py import crash, Cloudflare returns
 *            HTTP 502 Bad Gateway for every request to origin.  Chrome STILL
 *            logs every 502 in RED to the console even though the JS .catch()
 *            swallows it, so users file bugs about "red errors on sign-in
 *            page" that the catch-handler can never prevent.
 *
 *  Final production fix (v4): NO network calls EVER from the login page clock.
 *  Skew is always 0.  The LiveClock component simply uses the device clock,
 *  the synchronised green pulse dot is always shown, and nothing can ever
 *  produce a console-level error from this code path again.
 *
 *  Authoritative server time is used ONLY server-side (during login response
 *  generation, punch-clock recording, attendance calculation) where it
 *  actually matters for audit correctness.
 */

export function LiveClock() {
  // Skew in ms.  Always 0 on the sign-in page.  Kept as a variable so the same
  // render path remains structurally identical to the admin dashboard variant.
  const skew = 0;
  const synced = true;
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const d = new Date(tick + skew + OFFSET_MS);
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  const date = `${DAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCDate())} ${d.getUTCFullYear()}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      title="Synchronised with local clock"
      className="flex items-center gap-3 rounded-full border border-azure-400/25 bg-navy-900/50 py-2 pr-4 pl-3 font-mono text-[11px] tracking-[0.16em] backdrop-blur-md"
    >
      <span className="animate-pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
      <span className="text-white/95 tabular-nums">{time}</span>
      <span className="text-azure-300/50">·</span>
      <span className="text-azure-300/75">{date}</span>
    </motion.div>
  );
}

export function BrandMark() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="flex items-center gap-3"
    >
      <div className="relative grid h-10 w-10 place-items-center rounded-xl bg-sun-400 shadow-[0_0_36px_rgba(255,210,31,0.35)]">
        <Hexagon className="h-5.5 w-5.5 text-navy-950" strokeWidth={2.4} />
        <span className="absolute -right-1 -bottom-1 h-2.5 w-2.5 rounded-full border-2 border-navy-950 bg-azure-500" />
      </div>
      <div>
        <div className="font-display text-base leading-tight font-bold tracking-tight text-white">
          MERIDIAN
        </div>
        <div className="font-mono text-[8px] tracking-[0.32em] text-azure-300/70">
          ENTERPRISE CLOUD
        </div>
      </div>
    </motion.div>
  );
}

export function ReactiveBackground() {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.4);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const spotlight = useTransform([sx, sy], ([x, y]) =>
    `radial-gradient(680px circle at ${Number(x) * 100}% ${Number(y) * 100}%, rgba(46,107,255,0.2), transparent 65%)`
  );
  const counter = useTransform([sx, sy], ([x, y]) =>
    `radial-gradient(420px circle at ${100 - Number(x) * 100}% ${100 - Number(y) * 100}%, rgba(255,210,31,0.05), transparent 60%)`
  );

  useEffect(() => {
    const onMove = (e) => {
      if (typeof e?.clientX !== "number" || typeof window?.innerWidth !== "number") return;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      mx.set(Math.min(1, Math.max(0, e.clientX / w)));
      my.set(Math.min(1, Math.max(0, e.clientY / h)));
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [mx, my]);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-950 to-[#03060f]" />
      <div className="bg-grid-faint animate-grid-drift absolute inset-0 opacity-70 [mask-image:radial-gradient(ellipse_at_50%_35%,black_25%,transparent_78%)]" />
      <ParticleField />
      <motion.div className="absolute inset-0" style={{ background: spotlight }} />
      <motion.div className="absolute inset-0" style={{ background: counter }} />
      <div className="animate-float-slow absolute -top-32 right-[8%] h-96 w-96 rounded-full bg-azure-600/20 blur-3xl" />
      <div className="animate-float-slow absolute -bottom-40 left-[6%] h-[26rem] w-[26rem] rounded-full bg-azure-500/10 blur-3xl [animation-delay:-4.5s]" />
      <div className="bg-noise absolute inset-0 opacity-[0.05] mix-blend-overlay" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(4,8,23,0.75)_100%)]" />
    </div>
  );
}
